import PageLoader from 'components/GeneralComponents/Loaders/PageLoader'
import PrivateRoute from 'components/LayoutComponents/PrivateRoute'
import { ConnectedRouter } from 'connected-react-router'
import IndexLayout from 'layouts'
import NotFoundPage from 'pages/Minimal/404'
import React from 'react'
import Loadable from 'react-loadable'
import { Route, Switch } from 'react-router-dom'
import ApiContext from 'services/ApiContext'
import { useIntl } from 'react-intl'
import TestPage from 'pages/Minimal/TestPage'
import { DEFAULT_ROLES } from 'utils/constants'
import { restrictions } from 'utils/enums'

const { NO_ADMIN, NO_PREMIUM, NO_GROUP } = restrictions

export const isAdmin = (user) => user && user.userStatus === 'ADMIN'

const loadable = (loader) =>
  Loadable({
    loader,
    delay: false,
    loading: () => <PageLoader />,
  })

const routes = [
  // System Pages
  {
    path: '/error-log',
    component: loadable(() => import('pages/Minimal/ErrorPage')),
    title: 'errorPage.pathName',
  },
  {
    path: '/access-denied',
    component: loadable(() => import('pages/Minimal/AccessDeniedPage')),
    title: 'accessDeniedPage.pathName',
  },
  {
    path: '/404',
    component: loadable(() => import('pages/Minimal/404')),
    title: '404.pathName',
  },
  {
    path: '/under-construction',
    component: loadable(() => import('pages/Minimal/UnderConstructionPage')),
    title: 'underConstructionPage.pathName',
  },
  {
    path: '/accept',
    component: loadable(() => import('pages/Minimal/AcceptProjectInvitation')),
    title: 'projectInvitationPage.pathName',
  },
  {
    path: '/accept-group',
    component: loadable(() => import('pages/Minimal/AcceptGroupInvitation')),
    title: 'groupInvitationPage.pathName',
  },
  {
    path: '/signup-successful',
    component: loadable(() => import('pages/Minimal/VerifyEmailPage')),
    title: 'verifyEmailPage.pathName',
  },
  {
    path: '/employee-dashboard',
    component: loadable(() => import('pages/Dashboard/EmployeeDashboardPage')),
    title: 'employeeDashboardPage.pathName',
  },
]

const privateRoutes = [
  // Dashboards
  {
    path: '/overview',
    component: loadable(() => import('pages/Dashboard/OverviewPage')),
    title: 'overview.pathName',
  },
  {
    path: '/securities',
    component: loadable(() => import('pages/Editing/SecuritiesPage')),
    title: 'securities.pathName',
  },
  {
    path: '/cap-table',
    component: loadable(() => import('pages/Dashboard/CapTablePage')),
    title: 'capTable.pathName',
  },
  {
    path: '/waterfall-analysis',
    component: loadable(() => import('pages/Dashboard/WaterfallPage')),
    title: 'waterfall.pathName',
    restrictAccess: (user, project) => (!project || !project.hasPremiumAccess ? NO_PREMIUM : false),
    sampleImage: '/resources/images/page_samples/waterfall.png',
  },
  {
    path: '/portfolio',
    component: loadable(() => import('pages/Dashboard/PortfolioPage')),
    title: 'portfolio.pathName',
  },
  {
    path: '/documents',
    component: loadable(() => import('pages/Dashboard/DocumentsPage')),
    title: 'documents.pathName',
  },

  // Editing pages
  {
    path: '/guide',
    component: loadable(() => import('pages/Editing/GuidedTourPage')),
    title: 'guidedTour.pathName',
  },
  {
    path: '/edit',
    component: loadable(() => import('pages/Editing/GuidedTourPage')),
    title: 'edit.pathName',
  },
  {
    path: '/new-round',
    component: loadable(() => import('pages/Editing/NewRoundPage')),
    title: 'newRound.pathName',
  },
  {
    path: '/stakeholders',
    component: loadable(() => import('pages/Editing/StakeholdersPage')),
    title: 'stakeholdersPage.pathName',
  },
  {
    path: '/esop',
    component: loadable(() => import('pages/Editing/EsopPage/EsopOverview')),
    title: 'esop.pathName',
  },
  {
    path: '/grants-overview',
    component: loadable(() => import('pages/Editing/EsopPage/GrantsOverview')),
    title: 'grantsOverview.pathName',
  },
  {
    path: '/grant-modelling',
    component: loadable(() => import('pages/Editing/EsopPage/GrantCalculator')),
    title: 'grantModelling.pathName',
  },
  {
    path: '/vesting',
    component: loadable(() => import('pages/Editing/VestingPage')),
    title: 'vesting.pathName',
  },
  {
    path: '/group',
    component: loadable(() => import('pages/Editing/GroupPage')),
    restrictAccess: (user) => (!user || !user.group ? NO_GROUP : false),
    title: 'groupPage.pathName',
  },

  // Modelling pages
  {
    path: '/modelling',
    component: loadable(() => import('pages/Modelling/ModellingOverviewPage')),
    title: 'modellingPage.pathName',
    fadeIn: false,
  },
  {
    path: '/modelling-edit',
    component: loadable(() => import('pages/Modelling/ModellingEditPage')),
    title: 'modellingPage.pathName',
    restrictAccess: (user, project) => (!project || !project.hasPremiumAccess ? NO_PREMIUM : false),
    sampleImage: '/resources/images/page_samples/modelling.png',
  },

  // Admin pages
  {
    path: '/bjr',
    component: loadable(() => import('pages/Minimal/BjrPage')),
    title: 'Business judgement rule',
    restrictAccess: (user) => (!isAdmin(user) ? NO_ADMIN : false),
  },
  {
    path: '/admin-dashboard',
    component: loadable(() => import('pages/Minimal/AdminDashboardPage')),
    title: 'Admin Dashboard',
    restrictAccess: (user) => (!isAdmin(user) ? NO_ADMIN : false),
  },

  // Minimal pages
  {
    path: '/billing-success',
    component: loadable(() => import('pages/Minimal/BillingSuccessfulPage')),
    title: 'billing.pathName',
  },
  {
    path: '/billing',
    component: loadable(() => import('pages/Minimal/BillingPage')),
    title: 'billing.pathName',
  },
  {
    path: '/profile',
    component: loadable(() => import('pages/Minimal/ProfilePage')),
    title: 'userProfile.pathName',
  },
  {
    path: '/report-bug',
    component: loadable(() => import('pages/Minimal/ReportBugPage')),
    title: 'bugReportPage.pathName',
  },
  {
    path: '/changelog',
    component: loadable(() => import('pages/Minimal/ChangelogPage')),
    title: 'changelogPage.pathName',
  },
  {
    path: '/revisions',
    component: loadable(() => import('pages/Dashboard/RevisionPage')),
    title: 'revisionPage.pathName',
  },

  // HOME
  {
    path: '/',
    component: loadable(() => import('pages/Dashboard/OverviewPage')),
    title: 'overview.pathName',
  },
]

export const needsNoAuth0Login = ['/employee-dashboard']
// Pages allowed to see without a project (e.g. minimal)
export const needsNoProjectPages = [
  '/guide',
  '/billing',
  '/billing-success',
  '/profile',
  '/group',
  '/portfolio',
  '/report-bug',
  'changelog',
]
// Pages allowed to see without a company/round set up
export const needsNoCapTablePages = [...needsNoProjectPages, '/edit']

const Router = (props) => {
  const { history } = props
  const intl = useIntl()

  // TODO: Workaround until all translated
  const getTitle = (t) => (intl.messages[t] ? intl.formatMessage({ id: t }) : t)

  return (
    <ConnectedRouter history={history}>
      <ApiContext>
        <IndexLayout>
          <Switch>
            <Route path="/test" render={() => <TestPage />} />
            {routes.map((route) => (
              <Route
                path={route.path}
                component={route.component}
                key={route.path}
                exact={route.exact}
                title={getTitle(route.title)}
              />
            ))}
            {privateRoutes.map((route) => (
              <PrivateRoute
                path={route.path}
                component={route.component}
                key={route.path}
                exact={route.exact}
                title={getTitle(route.title)}
                sampleImage={route.sampleImage}
                fadeIn={route.fadeIn}
                restrictAccess={route.restrictAccess}
                projectRoles={route.projectRoles || DEFAULT_ROLES.projectRoles}
              />
            ))}
            <Route component={NotFoundPage} />
          </Switch>
        </IndexLayout>
      </ApiContext>
    </ConnectedRouter>
  )
}

export default Router
